import {
    AccountCircle,
    CardMembership as SubscriptionIcon,
    SmartToy as AgentsIcon,
    QuestionAnswer as FAQIcon,
    WhatsApp as WhatsAppIcon,
    Psychology as GenesisIcon,
    School as SkillsCoachIcon,
    OpenInNew as ExternalLinkIcon
} from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Grid,
    Modal,
    TextField,
    Typography,
    Chip
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscriptionContext } from '../contexts/SubscriptionContext';
import userService from '../services/userService';

const DashboardPage = () => {
    const { user } = useAuth();
    const { canManageFAQs, canConfigureWhatsApp, canAccessGenesis, canAccessSkillsCoach, currentSubscription } = useSubscriptionContext();
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loadingProfile, setLoadingProfile] = useState(true);
    const [error, setError] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [formData, setFormData] = useState({ first_name: '', last_name: '' });

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const profileData = await userService.getUserProfile();
                setProfile(profileData);
            } catch (err) {
                setError('Impossible de charger les informations du profil.');
                console.error(err);
            } finally {
                setLoadingProfile(false);
            }
        };

        fetchProfile();
    }, []);

    useEffect(() => {
        if (profile) {
            setFormData({
                first_name: profile.first_name || '',
                last_name: profile.last_name || ''
            });
        }
    }, [profile]);

    const handleOpenModal = () => setOpenModal(true);
    const handleCloseModal = () => setOpenModal(false);

    const handleFormChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        try {
            const updatedProfile = await userService.updateUserProfile(formData);
            setProfile(updatedProfile);
            handleCloseModal();
        } catch (err) {
            setError('Erreur lors de la mise à jour du profil.');
            console.error(err);
        }
    };

    const goToSubscription = () => {
        navigate('/subscription');
    };

    const goToAgents = () => {
        navigate('/agents');
    };

    const goToFAQ = () => {
        navigate('/faq');
    };

    const goToWhatsAppConfig = () => {
        navigate('/whatsapp-config');
    };

    const goToWhatsAppConversations = () => {
        navigate('/whatsapp/conversations');
    };

    const goToGenesisCoaching = () => {
        // Redirection vers le frontend Genesis autonome (Hub & Satellites)
        const genesisUrl = import.meta.env.VITE_GENESIS_FRONTEND_URL || 'http://localhost:3002';
        window.open(genesisUrl, '_blank');
    };

    const goToSkillsCoach = () => {
        navigate('/skills-coach');
    };

    return (
        <>
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 3 } }}>
                <Grid container spacing={3}>
                    {/* Welcome Card */}
                    <Grid item xs={12}>
                        <Card
                            elevation={3}
                            sx={{
                                borderRadius: 3,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white'
                            }}
                        >
                            <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    textAlign: { xs: 'center', sm: 'left' },
                                    mb: 2
                                }}>
                                    <Avatar
                                        sx={{
                                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                                            mr: { xs: 0, sm: 2 },
                                            mb: { xs: 2, sm: 0 },
                                            width: 64,
                                            height: 64
                                        }}
                                    >
                                        <AccountCircle sx={{ fontSize: 40 }} />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                                            {user && user.email ? `Bienvenue, ${user.first_name || user.email} !` : 'Bienvenue sur DigitalCloud360 !'}
                                        </Typography>
                                        <Typography variant="h6" sx={{ opacity: 0.9 }}>
                                            Votre tableau de bord personnel est prêt à l'emploi.
                                        </Typography>
                                        {currentSubscription && (
                                            <Chip
                                                label={`Plan: ${currentSubscription.plan.name}`}
                                                sx={{
                                                    mt: 1,
                                                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                                                    color: 'white'
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* FAQ Management Card - Only if user can manage FAQs */}
                    {canManageFAQs && (
                        <Grid item xs={12} md={6}>
                            <Card
                                elevation={2}
                                sx={{
                                    borderRadius: 3,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        elevation: 4,
                                        transform: 'translateY(-2px)'
                                    },
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}
                            >
                                <CardContent sx={{
                                    p: 3,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flexGrow: 1
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Box sx={{
                                            bgcolor: 'success.main',
                                            borderRadius: '50%',
                                            p: 1,
                                            mr: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <FAQIcon sx={{ color: 'white' }} />
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Gestion FAQ WhatsApp
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3, flexGrow: 1 }}>
                                        Gérez vos questions-réponses, testez l'agent IA et consultez les statistiques d'utilisation.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        onClick={goToFAQ}
                                        fullWidth
                                        size="large"
                                        startIcon={<FAQIcon />}
                                        sx={{
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            mt: 'auto'
                                        }}
                                    >
                                        Accéder à la FAQ
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* Genesis AI Coach Card - Only if user can access Genesis */}
                    {canAccessGenesis && (
                        <Grid item xs={12} md={6}>
                            <Card
                                elevation={2}
                                sx={{
                                    borderRadius: 3,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        elevation: 4,
                                        transform: 'translateY(-2px)'
                                    },
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}
                            >
                                <CardContent sx={{
                                    p: 3,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flexGrow: 1
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Box sx={{
                                            bgcolor: 'secondary.main',
                                            borderRadius: '50%',
                                            p: 1,
                                            mr: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <GenesisIcon sx={{ color: 'white' }} />
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Genesis AI Coach
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3, flexGrow: 1 }}>
                                        Générez un Business Brief stratégique en quelques minutes grâce à l'IA.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={goToGenesisCoaching}
                                        fullWidth
                                        size="large"
                                        startIcon={<GenesisIcon />}
                                        endIcon={<ExternalLinkIcon fontSize="small" />}
                                        sx={{
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            mt: 'auto'
                                        }}
                                    >
                                        Lancer Genesis
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* Skills Coach Card - Only if user can access Skills Coach */}
                    {canAccessSkillsCoach && (
                        <Grid item xs={12} md={6}>
                            <Card
                                elevation={2}
                                sx={{
                                    borderRadius: 3,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        elevation: 4,
                                        transform: 'translateY(-2px)'
                                    },
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}
                            >
                                <CardContent sx={{
                                    p: 3,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flexGrow: 1
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Box sx={{
                                            bgcolor: 'primary.main',
                                            borderRadius: '50%',
                                            p: 1,
                                            mr: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <SkillsCoachIcon sx={{ color: 'white' }} />
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Skills Coach
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3, flexGrow: 1 }}>
                                        Évaluez et développez vos compétences avec l'IA Skills Coach.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={goToSkillsCoach}
                                        fullWidth
                                        size="large"
                                        startIcon={<SkillsCoachIcon />}
                                        sx={{
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            mt: 'auto'
                                        }}
                                    >
                                        Accéder à Skills Coach
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* WhatsApp Configuration Card - Only if user can configure WhatsApp */}
                    {canConfigureWhatsApp && (
                        <Grid item xs={12} md={6}>
                            <Card
                                elevation={2}
                                sx={{
                                    borderRadius: 3,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        elevation: 4,
                                        transform: 'translateY(-2px)'
                                    },
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}
                            >
                                <CardContent sx={{
                                    p: 3,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flexGrow: 1
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Box sx={{
                                            bgcolor: '#25D366',
                                            borderRadius: '50%',
                                            p: 1,
                                            mr: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <WhatsAppIcon sx={{ color: 'white' }} />
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Configuration WhatsApp
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3, flexGrow: 1 }}>
                                        Configurez votre intégration WhatsApp Business avec l'assistant IA en 5 étapes simples.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        onClick={goToWhatsAppConfig}
                                        fullWidth
                                        size="large"
                                        startIcon={<WhatsAppIcon />}
                                        sx={{
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            mt: 'auto',
                                            bgcolor: '#25D366',
                                            '&:hover': {
                                                bgcolor: '#128C7E'
                                            }
                                        }}
                                    >
                                        Configurer WhatsApp
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {canConfigureWhatsApp && (
                        <Grid item xs={12} md={6}>
                            <Card
                                elevation={2}
                                sx={{
                                    borderRadius: 3,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        elevation: 4,
                                        transform: 'translateY(-2px)'
                                    },
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}
                            >
                                <CardContent sx={{
                                    p: 3,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flexGrow: 1
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                        <Box sx={{
                                            bgcolor: 'primary.main',
                                            borderRadius: '50%',
                                            p: 1,
                                            mr: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <WhatsAppIcon sx={{ color: 'white' }} />
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            Conversations WhatsApp
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" color="textSecondary" sx={{ mb: 3, flexGrow: 1 }}>
                                        Consultez les messages de vos clients, répondez aux demandes en attente et autorisez la reprise de l'IA lorsque le sujet est traité.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={goToWhatsAppConversations}
                                        fullWidth
                                        size="large"
                                        startIcon={<WhatsAppIcon />}
                                        sx={{
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            mt: 'auto'
                                        }}
                                    >
                                        Voir les conversations
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    )}

                    {/* Quick Actions */}
                    <Grid item xs={12} md={6}>
                        <Card
                            elevation={2}
                            sx={{
                                borderRadius: 3,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    elevation: 4,
                                    transform: 'translateY(-2px)'
                                },
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <CardContent sx={{
                                p: 3,
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 1
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Box sx={{
                                        bgcolor: 'primary.main',
                                        borderRadius: '50%',
                                        p: 1,
                                        mr: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <SubscriptionIcon sx={{ color: 'white' }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Gestion des Abonnements
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 3, flexGrow: 1 }}>
                                    Gérez vos abonnements, consultez vos informations de facturation et suivez votre utilisation.
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={goToSubscription}
                                    fullWidth
                                    size="large"
                                    startIcon={<SubscriptionIcon />}
                                    sx={{
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        mt: 'auto'
                                    }}
                                >
                                    Accéder aux Abonnements
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Agents Management */}
                    <Grid item xs={12} md={6}>
                        <Card
                            elevation={2}
                            sx={{
                                borderRadius: 3,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    elevation: 4,
                                    transform: 'translateY(-2px)'
                                },
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <CardContent sx={{
                                p: 3,
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 1
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Box sx={{
                                        bgcolor: 'secondary.main',
                                        borderRadius: '50%',
                                        p: 1,
                                        mr: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <AgentsIcon sx={{ color: 'white' }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Gestion des Agents IA
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 3, flexGrow: 1 }}>
                                    Créez, configurez et supervisez vos agents intelligents pour automatiser vos interactions.
                                </Typography>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={goToAgents}
                                    fullWidth
                                    size="large"
                                    startIcon={<AgentsIcon />}
                                    sx={{
                                        borderRadius: 2,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        mt: 'auto'
                                    }}
                                >
                                    Gérer les Agents
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* User Info */}
                    <Grid item xs={12} md={6}>
                        <Card
                            elevation={2}
                            sx={{
                                borderRadius: 3,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    elevation: 4,
                                    transform: 'translateY(-2px)'
                                },
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <CardContent sx={{
                                p: 3,
                                display: 'flex',
                                flexDirection: 'column',
                                flexGrow: 1
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Box sx={{
                                        bgcolor: 'secondary.main',
                                        borderRadius: '50%',
                                        p: 1,
                                        mr: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <AccountCircle sx={{ color: 'white' }} />
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Informations du Compte
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                                    Consultez et gérez vos informations personnelles et de sécurité.
                                </Typography>
                                <Box sx={{ mb: 3, flexGrow: 1 }}>
                                    <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                                        <Box component="span" sx={{
                                            width: 8,
                                            height: 8,
                                            bgcolor: 'success.main',
                                            borderRadius: '50%',
                                            mr: 1
                                        }} />
                                        <strong>Email:</strong>&nbsp;{profile?.email || 'Chargement...'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box component="span" sx={{
                                            width: 8,
                                            height: 8,
                                            bgcolor: 'info.main',
                                            borderRadius: '50%',
                                            mr: 1
                                        }} />
                                        <strong>Nom d'utilisateur:</strong>&nbsp;{profile?.username || 'N/A'}
                                    </Typography>
                                </Box>
                                <Button variant="contained" onClick={handleOpenModal} sx={{ mt: 'auto' }}>Modifier le Profil</Button>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Container>
            <Modal
                open={openModal}
                onClose={handleCloseModal}
                aria-labelledby="edit-profile-modal-title"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    border: '2px solid #000',
                    boxShadow: 24,
                    p: 4,
                }}>
                    <Typography id="edit-profile-modal-title" variant="h6" component="h2">
                        Modifier le Profil
                    </Typography>
                    <Box component="form" onSubmit={handleFormSubmit} noValidate sx={{ mt: 1 }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="first_name"
                            label="Prénom"
                            name="first_name"
                            autoComplete="given-name"
                            autoFocus
                            value={formData.first_name}
                            onChange={handleFormChange}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="last_name"
                            label="Nom"
                            name="last_name"
                            autoComplete="family-name"
                            value={formData.last_name}
                            onChange={handleFormChange}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            sx={{ mt: 3, mb: 2 }}
                        >
                            Enregistrer les modifications
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default DashboardPage;
